// src/utils.ts

import type { Locale } from './types';
import { pick as pickLocale } from 'accept-language-parser'; // Using a library is recommended

// --- Local Storage ---

const isClientSide = typeof window !== 'undefined';

export function getStoredLocale(key: string): Locale | null {
  if (!isClientSide || !window.localStorage) {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.error('Error reading locale from localStorage:', error);
    return null;
  }
}

export function setStoredLocale(key: string, locale: Locale): void {
  if (!isClientSide || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(key, locale);
  } catch (error) {
    console.error('Error writing locale to localStorage:', error);
  }
}

// --- Header Parsing ---

/**
 * Parses the 'accept-language' header and finds the best match.
 * @param acceptLanguageHeader The value of the 'accept-language' header.
 * @param availableLocales An array of locales supported by the application.
 * @param defaultLocale The fallback locale.
 * @returns The best matching locale or the default locale.
 */
export function findBestMatchLocale(
  acceptLanguageHeader: string | null | undefined,
  availableLocales: readonly Locale[],
  defaultLocale: Locale
): Locale {
  if (!acceptLanguageHeader) {
    return defaultLocale;
  }

  try {
    // Use the accept-language-parser library
    const pickedLocale = pickLocale(
      [...availableLocales], // Needs to be mutable array for some versions
      acceptLanguageHeader,
      { loose: true } // Allows matching 'en' to 'en-US' etc.
    );

    return pickedLocale || defaultLocale;
  } catch (e) {
    console.warn(
      'Could not parse accept-language header:',
      acceptLanguageHeader,
      e
    );
    return defaultLocale;
  }
}

// Simple interpolation function
export function interpolate(str: string, values?: Record<string, any>): string {
  if (!values) {
    return str;
  }
  // Replace {key} with value[key]
  return str.replace(/\{(\w+)\}/g, (match, key) => {
    // Basic handling: return match if key not found or value is null/undefined
    const value = values[key];
    return value !== null && value !== undefined ? String(value) : match;
  });
}
